import React, { useState, useEffect } from 'react';
import { aiAPI, masterAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiZap, FiRefreshCw, FiSave, FiCheckCircle } from 'react-icons/fi';

const AIExamGenerator = () => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    tradegroup: '',
    trade: '',
    subject: '',
    chapter: '',
    topic: '',
    syllabus: '',
    num_questions: 10,
    language: 'english',
    difficulty: 'medium',
    question_type: 'mcq'
  });
  
  // Master data
  const [tradegroups, setTradegroups] = useState([]);
  const [trades, setTrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [chapters, setChapters] = useState([]);
  
  // Generated questions
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [showResults, setShowResults] = useState(false);
  
  // Load master data
  useEffect(() => {
    loadTradegroups();
  }, []);
  
  const loadTradegroups = async () => {
    try {
      const response = await masterAPI.getTradeGroups();
      if (response.data.success) {
        setTradegroups(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading trade groups:', error);
    }
  };
  
  const loadTrades = async (tradegroupId) => {
    try {
      const response = await masterAPI.getTradesByGroup(tradegroupId);
      if (response.data.success) {
        setTrades(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading trades:', error);
    }
  };
  
  const loadSubjects = async (tradegroup, trade) => {
    try {
      const response = await masterAPI.getSubjectsByTrade(tradegroup, trade);
      if (response.data.success) {
        setSubjects(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading subjects:', error);
    }
  };
  
  const loadChapters = async (tradegroup, trade, subject) => {
    try {
      const response = await masterAPI.getChaptersBySubject(tradegroup, trade, subject);
      if (response.data.success) {
        setChapters(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading chapters:', error);
    }
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Handle cascading dropdowns
    if (name === 'tradegroup') {
      setFormData(prev => ({ ...prev, trade: '', subject: '', chapter: '' }));
      setTrades([]);
      setSubjects([]);
      setChapters([]);
      if (value) loadTrades(value);
    } else if (name === 'trade') {
      setFormData(prev => ({ ...prev, subject: '', chapter: '' }));
      setSubjects([]);
      setChapters([]);
      if (value && formData.tradegroup) {
        loadSubjects(formData.tradegroup, value);
      }
    } else if (name === 'subject') {
      setFormData(prev => ({ ...prev, chapter: '' }));
      setChapters([]);
      if (value && formData.tradegroup && formData.trade) {
        loadChapters(formData.tradegroup, formData.trade, value);
      }
    }
  };
  
  const handleGenerate = async (e) => {
    e.preventDefault();
    
    if (!formData.topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }
    
    setGenerating(true);
    
    try {
      const response = await aiAPI.generateExam(formData);
      
      if (response.data.success) {
        setGeneratedQuestions(response.data.questions);
        setShowResults(true);
        toast.success(`Generated ${response.data.count} questions successfully!`);
        
        // Scroll to results
        setTimeout(() => {
          document.getElementById('results-section')?.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }, 300);
      } else {
        toast.error(response.data.message || 'Failed to generate questions');
      }
    } catch (error) {
      console.error('Generation error:', error);
      toast.error(error.response?.data?.message || 'Error generating questions');
    } finally {
      setGenerating(false);
    }
  };
  
  const handleSaveQuestions = async () => {
    if (generatedQuestions.length === 0) {
      toast.error('No questions to save');
      return;
    }
    
    if (!window.confirm(`Save ${generatedQuestions.length} questions to question bank?`)) {
      return;
    }
    
    setSaving(true);
    
    try {
      const response = await aiAPI.saveQuestions({
        questions: generatedQuestions,
        tradegroup: formData.tradegroup,
        trade: formData.trade,
        subject: formData.subject,
        chapter: formData.chapter,
        topic: formData.topic,
        language: formData.language,
        difficulty: formData.difficulty
      });
      
      if (response.data.success) {
        toast.success(`${response.data.count} questions saved successfully!`);
        // Reset form
        setGeneratedQuestions([]);
        setShowResults(false);
        setFormData(prev => ({ ...prev, topic: '', syllabus: '' }));
      } else {
        toast.error(response.data.message || 'Failed to save questions');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error(error.response?.data?.message || 'Error saving questions');
    } finally {
      setSaving(false);
    }
  };
  
  const handleRegenerate = () => {
    setShowResults(false);
    setGeneratedQuestions([]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const isBilingual = formData.language === 'bilingual';
  
  return (
    <div className="p-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-8 mb-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <FiZap className="text-3xl" />
          <h1 className="text-3xl font-bold">AI Exam Generator</h1>
        </div>
        <p className="text-purple-100">
          Generate professional exam questions instantly using Artificial Intelligence. 
          Simply provide a topic and let AI create high-quality questions with explanations.
        </p>
      </div>
      
      {/* Configuration Form */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
          <FiZap className="text-purple-600" />
          Exam Configuration
        </h2>
        
        <form onSubmit={handleGenerate}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Trade Group */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Trade Group <span className="text-red-500">*</span>
              </label>
              <select
                name="tradegroup"
                value={formData.tradegroup}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              >
                <option value="">Select Trade Group</option>
                {tradegroups.map(tg => (
                  <option key={tg.id} value={tg.id}>{tg.name}</option>
                ))}
              </select>
            </div>
            
            {/* Trade */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Trade <span className="text-red-500">*</span>
              </label>
              <select
                name="trade"
                value={formData.trade}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
                disabled={!formData.tradegroup}
              >
                <option value="">Select Trade</option>
                {trades.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>
            
            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subject (Optional)
              </label>
              <select
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                disabled={!formData.trade}
              >
                <option value="">Select Subject</option>
                {subjects.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
            
            {/* Chapter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Chapter (Optional)
              </label>
              <select
                name="chapter"
                value={formData.chapter}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                disabled={!formData.subject}
              >
                <option value="">Select Chapter</option>
                {chapters.map(ch => (
                  <option key={ch.id} value={ch.id}>{ch.name}</option>
                ))}
              </select>
            </div>
            
            {/* Topic */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Topic / Chapter Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="topic"
                value={formData.topic}
                onChange={handleChange}
                placeholder="e.g., Supply and Demand, Microeconomics, Economic Growth..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">Enter the topic you want to generate questions about</p>
            </div>
            
            {/* Syllabus */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Detailed Syllabus / Content (Optional)
              </label>
              <textarea
                name="syllabus"
                value={formData.syllabus}
                onChange={handleChange}
                rows="4"
                placeholder="Paste chapter content, key points, or important concepts here..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">Providing more context helps AI generate better questions</p>
            </div>
            
            {/* Number of Questions */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Number of Questions
              </label>
              <input
                type="number"
                name="num_questions"
                value={formData.num_questions}
                onChange={handleChange}
                min="1"
                max="50"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              />
            </div>
            
            {/* Language */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Language
              </label>
              <select
                name="language"
                value={formData.language}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              >
                <option value="english">English</option>
                <option value="hindi">Hindi (हिंदी)</option>
                <option value="bilingual">Bilingual (English + Hindi)</option>
                <option value="hinglish">Hinglish (Hindi + English)</option>
              </select>
            </div>
            
            {/* Difficulty */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Difficulty Level
              </label>
              <select
                name="difficulty"
                value={formData.difficulty}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              >
                <option value="easy">Easy - Basic/Fundamental</option>
                <option value="medium">Medium - Intermediate</option>
                <option value="hard">Hard - Advanced</option>
              </select>
            </div>
            
            {/* Question Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Question Type
              </label>
              <select
                name="question_type"
                value={formData.question_type}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                required
              >
                <option value="mcq">Multiple Choice (MCQ)</option>
                <option value="truefalse">True/False</option>
                <option value="mixed">Mixed (MCQ + True/False)</option>
              </select>
            </div>
          </div>
          
          {/* Generate Button */}
          <div className="mt-6">
            <button
              type="submit"
              disabled={generating}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-3 px-6 rounded-md hover:from-purple-700 hover:to-indigo-700 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-lg"
            >
              {generating ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Generating Questions...
                </>
              ) : (
                <>
                  <FiZap />
                  Generate Exam Questions
                </>
              )}
            </button>
          </div>
        </form>
      </div>
      
      {/* Generated Questions Section */}
      {showResults && generatedQuestions.length > 0 && (
        <div id="results-section" className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <FiCheckCircle className="text-green-600" />
              Generated Questions ({generatedQuestions.length})
            </h2>
            <div className="flex gap-2">
              <button
                onClick={handleRegenerate}
                className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors flex items-center gap-2"
              >
                <FiRefreshCw />
                Generate New
              </button>
              <button
                onClick={handleSaveQuestions}
                disabled={saving}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <FiSave />
                    Save to Question Bank
                  </>
                )}
              </button>
            </div>
          </div>
          
          <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
            <p className="text-blue-700">
              <strong>Success!</strong> Generated {generatedQuestions.length} questions. 
              Review them below and save to your question bank.
            </p>
          </div>
          
          {/* Questions List */}
          <div className="space-y-6">
            {generatedQuestions.map((q, index) => (
              <div key={index} className="bg-gray-50 border-l-4 border-purple-500 rounded-lg p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-purple-700">
                    Question {index + 1}
                  </h3>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                    q.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                    q.difficulty === 'hard' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {q.difficulty || 'medium'}
                  </span>
                </div>
                
                {/* Question Text */}
                {isBilingual && q.question_hindi ? (
                  <div className="mb-4">
                    <p className="text-base font-medium text-gray-900 mb-2">{q.question}</p>
                    <p className="text-sm text-gray-600 italic pl-3 border-l-2 border-purple-300">
                      {q.question_hindi}
                    </p>
                  </div>
                ) : (
                  <p className="text-base font-medium text-gray-900 mb-4">{q.question}</p>
                )}
                
                {/* Options */}
                <div className="space-y-2 mb-4">
                  {q.options?.map((option, idx) => (
                    <div
                      key={idx}
                      className={`p-3 rounded-md border-2 ${
                        option.isCorrect
                          ? 'bg-green-50 border-green-500'
                          : 'bg-white border-gray-200'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-gray-700">
                          {String.fromCharCode(65 + idx)}.
                        </span>
                        <div className="flex-1">
                          {isBilingual && option.text_hindi ? (
                            <>
                              <p className="text-gray-900">{option.text}</p>
                              <p className="text-sm text-gray-600 italic mt-1 pl-3">
                                {option.text_hindi}
                              </p>
                            </>
                          ) : (
                            <p className="text-gray-900">{option.text}</p>
                          )}
                        </div>
                        {option.isCorrect && (
                          <FiCheckCircle className="text-green-600 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Explanation */}
                {q.explanation && (
                  <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
                    <p className="font-semibold text-yellow-800 mb-1">💡 Explanation:</p>
                    {isBilingual && q.explanation_hindi ? (
                      <>
                        <p className="text-gray-800 mb-2">{q.explanation}</p>
                        <p className="text-sm text-gray-600 italic">
                          {q.explanation_hindi}
                        </p>
                      </>
                    ) : (
                      <p className="text-gray-800">{q.explanation}</p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AIExamGenerator;

